/**
 * 에이전트 대화 상태 — **공통 패키지 훅의 얇은 어댑터.**
 *
 * 예전에는 이 파일이 214줄짜리 상태 기계였다(SSE 12종 해석·스트리밍 버퍼·되묻기·계정).
 * 그 구현이 `@qbase/assistant-ui` 의 기준 구현이 됐고, 여기 남는 것은
 * **LiveSQL 의 호출 규약(`activeContext` · `settings`)을 공통 옵션으로 옮기는 일**뿐이다.
 *
 * AssistantPanel 은 한 줄도 바뀌지 않는다 — 인자도 반환값도 그대로다.
 */
import { useCallback } from 'react';
import { client } from '../services/assistantClient';

// 공통 패키지 — AI 제외 빌드에서는 번들에 넣지 않는다(services/assistantClient.js 와 같은 이유·같은 모양의 조건).
// 그 빌드에서는 패널이 그려지지 않아 이 훅이 불리지 않는다.
const shared = process.env.REACT_APP_NO_ASSISTANT === 'true' ? null : require('@qbase/assistant-ui');
const useShared = shared
  ? shared.useAssistantThread
  : () => { throw new Error('The AI assistant is not included in this build'); };

export default function useAssistantThread({ enabled, activeContext, settings }) {
  // 턴마다 서버로 보낼 도메인 컨텍스트. 공통 훅은 내용을 해석하지 않는다.
  const getContext = useCallback(() => ({
    connectionId: activeContext.connectionId || undefined,
    schema: activeContext.schema || undefined,
    editorSelection: settings.includeSelection
      ? (activeContext.getSelection ? activeContext.getSelection() : '')
      : '',
    model: settings.model,
    // 설정의 AI — 이어 갈 세션이 아직 없는 대화는 서버가 이것으로 옮긴다(threadRunner). 세션이 생긴 대화는 무시된다
    provider: settings.provider || 'claude',
    askTimeoutMin: settings.askTimeoutMin,
    // 스킬 수준 — 서버가 시스템 프롬프트의 어휘·보고서 형식을 여기 맞춘다
    skillLevel: settings.skillLevel,
    // 튜닝 수준 — **다른 축이다.** 서버가 이 상한 위의 도구를 아예 등록하지 않는다.
    tuningLevel: settings.tuningLevel,
    egress: { comments: settings.egressComments, sampleRows: settings.egressSampleRows },
    // 데이터모델·표준사전 (설정에서 지정). 비어 있으면 에이전트가 list_models 로 고르거나 되묻는다.
    stdNm: settings.stdNm || '',
    dataModelId: settings.dataModelId || '',
    // 튜닝 사례 원본 중 **이 메시지에 붙는 첨부**. 서버가 소유자를 확인하고 이미지는 블록으로, 텍스트는 프롬프트로 싣는다.
    sourceIds: settings.sourceIds || [],
    // 도움말 흐름에서 넘어온 턴의 힌트. 함수로 받아 **꺼내면서 지운다** — 다음 턴에 또 실리면 안 된다.
    helpHint: typeof settings.getHelpHint === 'function' ? (settings.getHelpHint() || undefined) : undefined,
    // 다른 창(사례 등록 화면 등)에서 보낸 요청이면 그 창의 지금 내용 { kind, text }. 도움말 힌트처럼 **꺼내면서 지운다**(utils/agentBridge)
    screenContext: typeof settings.getScreenContext === 'function' ? (settings.getScreenContext() || undefined) : undefined,
    // AI 를 바꿔 새 대화로 옮겼으면 앞 대화의 id — 서버가 그 기록을 새 대화의 첫 턴에만 실어 준다. 꺼내면서 지운다
    handoffFrom: typeof settings.getHandoff === 'function' ? (settings.getHandoff() || undefined) : undefined,
    // 지금 앱 화면 — 앞에 보이는 화면·열린 작업 화면·편집기 SQL·활성 결과 탭 요약(행 값 없음). 턴을 보낼 때의 모습을 읽는다
    workspace: typeof activeContext.getWorkspace === 'function' ? (activeContext.getWorkspace() || undefined) : undefined,
    // 리본에서 끈 정보원 — 서버가 해당 도구를 등록하지 않는다
    disabledMetaSources: settings.disabledMetaSources || [],
  }), [activeContext, settings]);

  return useShared({
    client,
    enabled,
    getContext,
    provider: settings.provider || 'claude',
    model: settings.model,
  });
}
